<template>
  <b-sidebar
    id="sidebar"
    :visible="isSidebarActive"
    bg-variant="white"
    sidebar-class="sidebar-lg"
    shadow
    backdrop
    no-header
    no-close-on-backdrop
    right
    @shown="loadData"
    @hidden="resetForm"
    @change="(val) => $emit('update:is-sidebar-active', val)"
  >
    <template #default="{ hide }">
      <div
        class="d-flex justify-content-between align-items-center content-sidebar-header px-2 py-1"
      >
        <h5 class="mb-0 text-capitalize">
          {{
            isAdd
              ? `${$t("actions.add")} ${$t("victronenergy.sites.title")}`
              : `${$t("actions.edit")} ${$t("victronenergy.sites.title")}`
          }}
        </h5>

        <feather-icon
          class="ml-1 cursor-pointer"
          icon="XIcon"
          size="16"
          @click="hide"
        />
      </div>

      <validation-observer #default="{ handleSubmit }" ref="refFormObserver">
        <b-form
          class="p-2"
          @submit.prevent="handleSubmit(onSubmit)"
          @reset.prevent="resetForm"
        >
          <validation-provider
            #default="validationContext"
            :name="$t('service_orders.facility')"
          >
            <b-form-group
              data-cy="facility"
              class="text-capitalize"
              :label="$t('service_orders.facility')"
              label-for="id_unit"
              :state="getValidationState(validationContext)"
            >
              <select-paginate-unit
                :id_unit.sync="id_unit"
                input-id="id_unit"
                :multiple="false"
              />
              <b-form-invalid-feedback
                :state="getValidationState(validationContext)"
              >
                {{ validationContext.errors[0] }}
              </b-form-invalid-feedback>
            </b-form-group>
          </validation-provider>

          <!-- Device tag -->
          <validation-provider
            #default="validationContext"
            :name="$t('dmp.tags')"
            rules="required"
          >
            <b-form-group
              class="text-capitalize"
              :label="$t('dmp.tags')"
              label-for="id_device_tag"
              :state="getValidationState(validationContext)"
            >
              <v-select
                v-model="itemData.id_device_tag"
                :options="tagOptions"
                :clearable="true"
                :reduce="(val) => val.value"
                label="name"
                input-id="id_device_tag"
              />
              <b-form-invalid-feedback
                :state="getValidationState(validationContext)"
              >
                {{ validationContext.errors[0] }}
              </b-form-invalid-feedback>
            </b-form-group>
          </validation-provider>

          <div class="d-flex mt-2">
            <b-button
              v-ripple.400="'rgba(255, 255, 255, 0.15)'"
              variant="primary"
              class="mr-2 text-capitalize"
              type="submit"
            >
              {{ $t("actions.save") }}
            </b-button>
            <b-button
              v-ripple.400="'rgba(186, 191, 199, 0.15)'"
              type="button"
              variant="outline-secondary"
              @click="hide"
              class="text-capitalize"
            >
              {{ $t("actions.cancel") }}
            </b-button>
          </div>
        </b-form>
      </validation-observer>
    </template>
  </b-sidebar>
</template>

<script>
import {
  BSidebar,
  BForm,
  BFormGroup,
  BFormInput,
  BFormInvalidFeedback,
  BButton,
  BFormCheckbox,
} from "bootstrap-vue";
import { ValidationProvider, ValidationObserver } from "vee-validate";
import vSelect from "vue-select";
import { ref } from "@vue/composition-api";
import { required, alphaNum, email, positive, required_if } from "@validations";
import { useToast } from "vue-toastification/composition";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";
import formValidation from "@core/comp-functions/forms/form-validation";
import Ripple from "vue-ripple-directive";
import store from "@/store";
import { useUtils as useI18nUtils } from "@core/libs/i18n";
import SelectPaginateUnit from "../../components/SelectPaginateUnit.vue";

export default {
  components: {
    BSidebar,
    BForm,
    BFormGroup,
    BFormInput,
    BFormInvalidFeedback,
    BButton,
    vSelect,
    BFormCheckbox,
    ValidationProvider,
    ValidationObserver,
    SelectPaginateUnit,
  },
  directives: {
    Ripple,
  },
  model: {
    prop: "isSidebarActive",
    event: "update:is-sidebar-active",
  },
  props: {
    isSidebarActive: {
      type: Boolean,
      required: true,
    },
    itemEdit: {
      type: Object,
      required: false,
    },
    unitOptions: {
      type: Array,
      required: true,
    },
    tagOptions: {
      type: Array,
      required: true,
    },
    isAdd: {
      type: Boolean,
      required: true,
    },
  },
  data() {
    return {
      required,
      alphaNum,
      email,
      positive,
    };
  },
  setup(props, { emit }) {
    const id_unit = ref();
    const { t } = useI18nUtils();
    const blankData = {
      id_unit: null,
      id_device_tag: null,
    };

    const toast = useToast();
    const itemData = ref(JSON.parse(JSON.stringify(blankData)));
    const resetData = () => {
      itemData.value = JSON.parse(JSON.stringify(blankData));
      id_unit.value = null;
    };

    // Normalizamos lo que venga de SelectPaginateUnit a un ID
    const resolveUnitId = () => {
      const unit = id_unit.value;
      console.log('resolveUnitId', unit)
      if (!unit) return null;

      // Multiple
      if (Array.isArray(unit)) {
        return unit;
      }

      // Si viene número o string directo
      if (typeof unit === "number" || typeof unit === "string") {
        return unit;
      }

      if (typeof unit === "object") {
        // { value: 123 }
        if (unit.value !== undefined && unit.value !== null) {
          return unit.value;
        }
        // { id: 123 }
        if (unit.id !== undefined && unit.id !== null) {
          return unit.id;
        }
        // { unit: 123, method: 'edit' }
        if (unit.unit !== undefined && unit.unit !== null) {
          return unit.unit;
        }
      }

      return null;
    };

    const onSubmit = () => {
      const unitId = resolveUnitId();
      console.log("UNIT ID ENVIADO:", unitId);
      let dispatch;

      if (props.isAdd) {
        dispatch = store.dispatch("victronenergy_sites/add", itemData.value);
      } else {
        const req = {
          id: itemData.value.id,
          params: {
            id_unit: unitId,
            id_device_tag: itemData.value.id_device_tag,
          },
        };

        dispatch = store.dispatch("victronenergy_sites/edit", req);
      }

      dispatch
        .then((response) => {
          if (response.success) {
            toast({
              component: ToastificationContent,
              props: {
                title: response.message,
                icon: "CheckIcon",
                variant: "success",
              },
            });
            emit("refetch-data");
            emit("update:is-sidebar-active", false);
          } else {
            toast({
              component: ToastificationContent,
              props: {
                title: response.message,
                icon: "AlertTriangleIcon",
                variant: "danger",
              },
            });
          }
        })
        .catch((response) => {
          toast({
            component: ToastificationContent,
            props: {
              title: response.response.data.message,
              icon: "AlertTriangleIcon",
              variant: "danger",
            },
          });
        });
    };

    const { refFormObserver, getValidationState, resetForm } =
      formValidation(resetData);

    const loadData = () => {
      if (!props.isAdd) {
        itemData.value = props.itemEdit;
        id_unit.value = itemData.value.camera?.units?.[0]?.id ?? null;
        itemData.value.id_device_tag = itemData.value.camera?.device_tag?.id ?? null;
      }
    };

    return {
      id_unit,
      itemData,
      onSubmit,
      refFormObserver,
      getValidationState,
      loadData,
      resetForm,
    };
  },
};
</script>